/**
 * Locale preference persistence.
 *
 * Personal: localStorage / session (durable user DB preference API is stub — gap documented).
 * Tenant default: read/write via tenants.settings.language when available; local mirror for UI.
 */

import { isSupportedLocale, normalizeLocaleInput, type SupportedLocale } from './localeCanon';

export const I18N_SESSION_LOCALE_KEY_V0 = 'inflomatrix.i18n.sessionLocale' as const;
export const I18N_USER_PREFERRED_LOCALE_KEY_V0 = 'inflomatrix.i18n.userPreferredLocale' as const;
export const I18N_TENANT_DEFAULT_LOCALE_KEY_V0 = 'inflomatrix.i18n.tenantDefaultLocale' as const;

export type PreferencePersistenceModeV0 =
  | 'session_and_local_storage'
  | 'tenant_settings_json_when_available'
  | 'durable_user_preference_deferred';

export const PREFERENCE_PERSISTENCE_DECISION_V0 = {
  personal: 'session_and_local_storage' as PreferencePersistenceModeV0,
  tenantDefault: 'tenant_settings_json_when_available' as PreferencePersistenceModeV0,
  durableUserPreferenceApi: 'durable_user_preference_deferred' as const,
  durableUserPreferenceComplete: false as const,
  notes: [
    'PersonalWorkbenchSettings sync API is stub — personal locale persists in localStorage only.',
    'Tenant default may map to tenants.settings.language when Configure UI/API is wired.',
    'Cross-tenant leakage must remain 0 — keys are namespaced; tenant default stored per tenantId when provided.',
  ],
};

function safeGet(storage: Storage | undefined, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSet(storage: Storage | undefined, key: string, value: string): void {
  try {
    storage?.setItem(key, value);
  } catch {
    // ignore quota / private mode
  }
}

function safeRemove(storage: Storage | undefined, key: string): void {
  try {
    storage?.removeItem(key);
  } catch {
    // ignore
  }
}

export function readSessionLocaleV0(): SupportedLocale | null {
  if (typeof sessionStorage === 'undefined') return null;
  return normalizeLocaleInput(safeGet(sessionStorage, I18N_SESSION_LOCALE_KEY_V0));
}

export function writeSessionLocaleV0(locale: SupportedLocale): void {
  if (typeof sessionStorage === 'undefined') return;
  safeSet(sessionStorage, I18N_SESSION_LOCALE_KEY_V0, locale);
}

export function clearSessionLocaleV0(): void {
  if (typeof sessionStorage === 'undefined') return;
  safeRemove(sessionStorage, I18N_SESSION_LOCALE_KEY_V0);
}

export function readUserPreferredLocaleV0(): SupportedLocale | null {
  if (typeof localStorage === 'undefined') return null;
  return normalizeLocaleInput(safeGet(localStorage, I18N_USER_PREFERRED_LOCALE_KEY_V0));
}

export function writeUserPreferredLocaleV0(locale: SupportedLocale): void {
  if (typeof localStorage === 'undefined') return;
  if (!isSupportedLocale(locale)) return;
  safeSet(localStorage, I18N_USER_PREFERRED_LOCALE_KEY_V0, locale);
  writeSessionLocaleV0(locale);
}

export function tenantDefaultStorageKeyV0(tenantId: string): string {
  return `${I18N_TENANT_DEFAULT_LOCALE_KEY_V0}:${tenantId}`;
}

export function readTenantDefaultLocaleV0(
  tenantId: string | null | undefined
): SupportedLocale | null {
  if (!tenantId || typeof localStorage === 'undefined') return null;
  return normalizeLocaleInput(safeGet(localStorage, tenantDefaultStorageKeyV0(tenantId)));
}

export function writeTenantDefaultLocaleV0(tenantId: string, locale: SupportedLocale): void {
  if (typeof localStorage === 'undefined') return;
  if (!isSupportedLocale(locale)) return;
  safeSet(localStorage, tenantDefaultStorageKeyV0(tenantId), locale);
}

/** Map legacy tenants.settings.language ('ko'|'en'|…) into SupportedLocale. */
export function mapTenantSettingsLanguageToLocaleV0(
  language: string | null | undefined
): SupportedLocale | null {
  return normalizeLocaleInput(language);
}
